// =============================================
// PANEL DE NOTIFICACIONES
// Control de envío de emails
// =============================================

import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  Send as SendIcon,
  Done as DoneIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import notificationService from '../../services/notificationService';
import userService from '../../services/userService';

const Notifications = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [testDialog, setTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [periodDialog, setPeriodDialog] = useState(false);
  const [periodData, setPeriodData] = useState({ year: new Date().getFullYear(), admin: '', adminId: null });
  const [statementDialog, setStatementDialog] = useState(false);
  const [statementData, setStatementData] = useState({ 
    month: new Date().getMonth() + 1, 
    year: new Date().getFullYear() 
  });
  const [owners, setOwners] = useState([]);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [selectAll, setSelectAll] = useState(false); // Cambiar a false por defecto
  const [loadingOwners, setLoadingOwners] = useState(false);

  // Test de email
  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Ingrese un email válido');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await notificationService.testEmail(testEmail);
      
      if (response.success) {
        alert('✅ Email de prueba enviado correctamente');
        setTestDialog(false);
        setTestEmail('');
      } else {
        setError(response.error || 'Error al enviar email');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar email de prueba');
    } finally {
      setLoading(false);
    }
  };

  // Recordatorios a morosos
  const handleDebtReminders = async () => {
    if (!window.confirm('¿Enviar recordatorios a TODOS los propietarios con deuda?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResults(null);
      
      const response = await notificationService.sendDebtReminders();
      setResults(response);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar recordatorios');
    } finally {
      setLoading(false);
    }
  };

  // Nuevo período
  const handleNewPeriod = async () => {
    if (!periodData.year || !periodData.adminId) {
      setError('Complete todos los campos');
      return;
    }

    if (selectedOwners.length === 0) {
      setError('Seleccione al menos un propietario');
      return;
    }

    const count = selectedOwners.length;

    if (!window.confirm(`¿Notificar nuevo período ${periodData.year} (Admin: ${periodData.admin}) a ${count} propietario${count !== 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResults(null);
      
      const response = await notificationService.sendNewPeriodNotification(
        periodData.year,
        periodData.admin,
        selectedOwners // Enviar IDs seleccionados
      );
      
      setResults(response);
      setPeriodDialog(false);
      setPeriodData({ year: new Date().getFullYear(), admin: '', adminId: null });
      setSelectAll(false);
      setSelectedOwners([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar notificación');
    } finally {
      setLoading(false);
    }
  };

  // Estados mensuales
  const handleMonthlyStatements = async () => {
    if (!statementData.month || !statementData.year) {
      setError('Complete todos los campos');
      return;
    }

    if (selectedOwners.length === 0) {
      setError('Seleccione al menos un propietario');
      return;
    }

    const count = selectedOwners.length;
    
    if (!window.confirm(`¿Enviar estados de cuenta de ${getMonthName(statementData.month)} ${statementData.year} a ${count} propietario${count !== 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResults(null);
      
      // Enviar IDs de propietarios seleccionados al backend
      const response = await notificationService.sendMonthlyStatements(
        statementData.month,
        statementData.year,
        selectedOwners // Enviar array de IDs
      );
      
      setResults(response);
      setStatementDialog(false);
      setSelectAll(false);
      setSelectedOwners([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar estados');
    } finally {
      setLoading(false);
    }
  };

  // Cargar propietarios
  const loadOwners = async () => {
    try {
      setLoadingOwners(true);
      const response = await userService.getOwners();
      const ownersList = response.owners || response.users || [];
      const ownersWithEmail = ownersList.filter(u => u.email);
      setOwners(ownersWithEmail);
      setSelectedOwners([]); // Iniciar sin selección
      setSelectAll(false); // Asegurar que "Todos" esté desmarcado
    } catch (err) {
      console.error('Error al cargar propietarios:', err);
      setError('Error al cargar lista de propietarios');
    } finally {
      setLoadingOwners(false);
    }
  };

  // Manejar selección de todos
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      // Si marca "Todos", seleccionar todos los owners
      setSelectedOwners(owners.map(o => o.id));
    } else {
      // Si desmarca "Todos", deseleccionar todos
      setSelectedOwners([]);
    }
  };

  // Manejar selección individual
  const handleToggleOwner = (ownerId) => {
    const currentIndex = selectedOwners.indexOf(ownerId);
    const newSelected = [...selectedOwners];

    if (currentIndex === -1) {
      newSelected.push(ownerId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedOwners(newSelected);
    
    // Actualizar "Todos" automáticamente
    if (newSelected.length === owners.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  };

  // Abrir dialog de estados y cargar propietarios
  const handleOpenStatementDialog = async () => {
    setStatementDialog(true);
    setError('');
    await loadOwners();
  };

  // Abrir dialog de nuevo período y cargar propietarios
  const handleOpenPeriodDialog = async () => {
    setPeriodDialog(true);
    setError('');
    await loadOwners();
  };

  const getMonthName = (month) => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return months[month - 1];
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          📧 Centro de Notificaciones
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Envío de emails automáticos a propietarios
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {results && (
        <Alert 
          severity={results.failed === 0 ? 'success' : 'warning'} 
          sx={{ mb: 3 }}
          onClose={() => setResults(null)}
        >
          <Typography variant="body1" fontWeight="bold">
            Emails enviados: {results.sent} ✅ | Fallidos: {results.failed} ❌
          </Typography>
          
          {results.results && (
            <List dense sx={{ mt: 2 }}>
              {results.results.map((result, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {result.status === 'sent' ? (
                      <DoneIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={result.owner || result.email}
                    secondary={result.email}
                  />
                  <Chip 
                    label={result.status === 'sent' ? 'Enviado' : 'Error'}
                    size="small"
                    color={result.status === 'sent' ? 'success' : 'error'}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Test de Email */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">Probar Configuración</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enviar email de prueba
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                Verifica que la configuración de email está funcionando correctamente.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                variant="outlined" 
                fullWidth
                onClick={() => setTestDialog(true)}
                disabled={loading}
              >
                Probar Email
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Recordatorios a Morosos */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">Recordatorios</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Notificar a morosos
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                Envía recordatorio de pago a todos los propietarios con cuotas pendientes.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                variant="contained" 
                color="warning"
                fullWidth
                startIcon={<SendIcon />}
                onClick={handleDebtReminders}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Enviar Recordatorios'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Nuevo Período */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">Nuevo Período</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Notificar apertura
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                Informa a todos los propietarios sobre la apertura de un nuevo período.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                variant="outlined" 
                color="info"
                fullWidth
                onClick={handleOpenPeriodDialog}
                disabled={loading}
              >
                Notificar Período
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Estados Mensuales */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">Estados Mensuales</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Resumen de cuenta
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                Envía estado de cuenta mensual a todos los propietarios.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                variant="outlined" 
                color="success"
                fullWidth
                onClick={handleOpenStatementDialog}
                disabled={loading}
              >
                Enviar Estados
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog: Test Email */}
      <Dialog open={testDialog} onClose={() => setTestDialog(false)}>
        <DialogTitle>🧪 Probar Configuración de Email</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email de Prueba"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="ejemplo@correo.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog(false)}>Cancelar</Button>
          <Button onClick={handleTestEmail} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Nuevo Período */}
      <Dialog 
        open={periodDialog} 
        onClose={() => {
          setPeriodDialog(false);
          setSelectAll(false);
          setSelectedOwners([]);
          setPeriodData({ year: new Date().getFullYear(), admin: '', adminId: null });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '85vh',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>📅 Notificar Nuevo Período</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Año"
                type="number"
                size="small"
                value={periodData.year}
                onChange={(e) => setPeriodData({ ...periodData, year: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Administrador"
                size="small"
                value={periodData.adminId || ''}
                onChange={(e) => {
                  const selectedOwner = owners.find(o => o.id === parseInt(e.target.value));
                  setPeriodData({ 
                    ...periodData, 
                    adminId: parseInt(e.target.value),
                    admin: selectedOwner ? selectedOwner.name : ''
                  });
                }}
                SelectProps={{ native: true }}
              >
                <option value="">Seleccione...</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name} - Casa #{owner.house_number}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 1 }}>
            Destinatarios
          </Typography>

          {/* Checkbox "Todos" */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Seleccionar todos
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {owners.length} propietarios disponibles
                </Typography>
              </Box>
            }
            sx={{ mb: 1 }}
          />

          {/* Lista de propietarios */}
          <Box sx={{ 
            maxHeight: 250, 
            overflow: 'auto', 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 1.5,
            bgcolor: '#fafafa'
          }}>
            {loadingOwners ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : owners.length === 0 ? (
              <Alert severity="warning" sx={{ m: 1 }}>
                No hay propietarios con email registrado
              </Alert>
            ) : (
              owners.map((owner) => (
                <Box 
                  key={owner.id}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedOwners.indexOf(owner.id) !== -1}
                    onChange={() => handleToggleOwner(owner.id)}
                    size="small"
                    sx={{ mt: -0.5 }}
                  />
                  <Box sx={{ ml: 1, flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {owner.name} - Casa #{owner.house_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {owner.email}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {selectedOwners.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>{selectedOwners.length}</strong> propietario{selectedOwners.length !== 1 ? 's' : ''} seleccionado{selectedOwners.length !== 1 ? 's' : ''}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setPeriodDialog(false);
              setSelectAll(false);
              setSelectedOwners([]);
              setPeriodData({ year: new Date().getFullYear(), admin: '', adminId: null });
            }}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleNewPeriod} 
            variant="contained" 
            disabled={loading || !periodData.year || !periodData.adminId || selectedOwners.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : `Enviar (${selectedOwners.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Estados Mensuales */}
      <Dialog 
        open={statementDialog} 
        onClose={() => {
          setStatementDialog(false);
          setSelectAll(false);
          setSelectedOwners([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '85vh',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>📊 Enviar Estados de Cuenta</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Mes"
                size="small"
                value={statementData.month}
                onChange={(e) => setStatementData({ ...statementData, month: e.target.value })}
                SelectProps={{ native: true }}
              >
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {getMonthName(i + 1)}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Año"
                size="small"
                type="number"
                value={statementData.year}
                onChange={(e) => setStatementData({ ...statementData, year: e.target.value })}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 1 }}>
            Destinatarios
          </Typography>

          {/* Checkbox "Todos" */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Seleccionar todos
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {owners.length} propietarios disponibles
                </Typography>
              </Box>
            }
            sx={{ mb: 1 }}
          />

          {/* Lista de propietarios */}
          <Box sx={{ 
            maxHeight: 350, // Aumentar altura
            overflow: 'auto', 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 1.5,
            bgcolor: '#fafafa'
          }}>
            {loadingOwners ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : owners.length === 0 ? (
              <Alert severity="warning" sx={{ m: 1 }}>
                No hay propietarios con email registrado
              </Alert>
            ) : (
              owners.map((owner) => (
                <Box 
                  key={owner.id}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedOwners.indexOf(owner.id) !== -1}
                    onChange={() => handleToggleOwner(owner.id)}
                    size="small"
                    sx={{ mt: -0.5 }}
                  />
                  <Box sx={{ ml: 1, flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {owner.name} - Casa #{owner.house_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {owner.email}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {selectedOwners.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>{selectedOwners.length}</strong> propietario{selectedOwners.length !== 1 ? 's' : ''} seleccionado{selectedOwners.length !== 1 ? 's' : ''}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setStatementDialog(false);
              setSelectAll(false);
              setSelectedOwners([]);
            }}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleMonthlyStatements} 
            variant="contained" 
            disabled={loading || selectedOwners.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : `Enviar (${selectedOwners.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Notifications;
